/**
 * PuppetEditForm Component (Task 5.5)
 *
 * Form for editing puppet properties displayed in the right column header.
 *
 * Features:
 * - Fields: Name, Model Path, Description, URL
 * - Real-time validation (name required, model path required)
 * - Auto-save on blur or explicit save button
 */

import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Check, AlertCircle } from 'lucide-react';
import { usePuppetStore, type Puppet } from '@/stores/puppetStore';

interface PuppetEditFormProps {
  /**
   * The puppet to edit
   */
  puppet: Puppet;
}

interface FormErrors {
  name?: string;
  modelPath?: string;
}

export function PuppetEditForm({ puppet }: PuppetEditFormProps) {
  const updatePuppet = usePuppetStore((state) => state.updatePuppet);

  // Local form state
  const [name, setName] = useState(puppet.name);
  const [modelPath, setModelPath] = useState(puppet.modelPath);
  const [description, setDescription] = useState(puppet.description || '');
  const [url, setUrl] = useState(puppet.url || '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [hasChanges, setHasChanges] = useState(false);

  // Reset form when puppet changes (syncing form state from external puppet prop)
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setName(puppet.name);
    setModelPath(puppet.modelPath);
    setDescription(puppet.description || '');
    setUrl(puppet.url || '');
    setErrors({});
    setHasChanges(false);
  }, [
    puppet.id,
    puppet.name,
    puppet.modelPath,
    puppet.description,
    puppet.url,
  ]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Validate form fields
  const validate = useCallback((): boolean => {
    const newErrors: FormErrors = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    // Model path is optional but should be valid if provided
    // No validation needed for empty model path

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [name]);

  // Save changes to store
  const saveChanges = useCallback(() => {
    if (!validate()) {
      return;
    }

    updatePuppet(puppet.id, {
      name: name.trim(),
      modelPath: modelPath.trim(),
      description: description.trim() || undefined,
      url: url.trim() || undefined,
    });

    setHasChanges(false);
  }, [puppet.id, name, modelPath, description, url, updatePuppet, validate]);

  // Auto-save on blur
  const handleBlur = useCallback(() => {
    if (hasChanges) {
      saveChanges();
    }
  }, [hasChanges, saveChanges]);

  // Track changes
  const handleNameChange = (value: string) => {
    setName(value);
    setHasChanges(true);
    if (errors.name && value.trim()) {
      setErrors((prev) => ({ ...prev, name: undefined }));
    }
  };

  const handleModelPathChange = (value: string) => {
    setModelPath(value);
    setHasChanges(true);
  };

  const handleDescriptionChange = (value: string) => {
    setDescription(value);
    setHasChanges(true);
  };

  const handleUrlChange = (value: string) => {
    setUrl(value);
    setHasChanges(true);
  };

  return (
    <div className="space-y-4">
      {/* Puppet name field */}
      <div>
        <label
          htmlFor="puppet-name"
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Name <span className="text-red-400">*</span>
        </label>
        <div className="relative">
          <input
            id="puppet-name"
            type="text"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            onBlur={handleBlur}
            className="w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
            style={{
              backgroundColor: 'var(--color-dark-bg)',
              borderColor: errors.name
                ? 'var(--color-body-control)'
                : 'var(--color-dark-border)',
              color: 'var(--color-dark-text)',
            }}
            placeholder="Enter puppet name"
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'puppet-name-error' : undefined}
          />
          {errors.name && (
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              <AlertCircle
                className="h-4 w-4"
                style={{ color: 'var(--color-body-control)' }}
              />
            </div>
          )}
        </div>
        {errors.name && (
          <p
            id="puppet-name-error"
            className="mt-1 text-xs"
            style={{ color: 'var(--color-body-control)' }}
          >
            {errors.name}
          </p>
        )}
      </div>

      {/* Model path field */}
      <div>
        <label
          htmlFor="puppet-model-path"
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Model Path
        </label>
        <input
          id="puppet-model-path"
          type="text"
          value={modelPath}
          onChange={(e) => handleModelPathChange(e.target.value)}
          onBlur={handleBlur}
          className="w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
          style={{
            backgroundColor: 'var(--color-dark-bg)',
            borderColor: 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          placeholder="/models/puppet.glb"
        />
        <p
          className="mt-1 text-xs"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Path to the 3D model file (optional)
        </p>
      </div>

      {/* Description field */}
      <div>
        <label
          htmlFor="puppet-description"
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Description
        </label>
        <textarea
          id="puppet-description"
          value={description}
          onChange={(e) => handleDescriptionChange(e.target.value)}
          onBlur={handleBlur}
          rows={2}
          className="w-full resize-none rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
          style={{
            backgroundColor: 'var(--color-dark-bg)',
            borderColor: 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          placeholder="Enter a description for this puppet"
        />
      </div>

      {/* URL field */}
      <div>
        <label
          htmlFor="puppet-url"
          className="mb-1 block text-xs font-medium"
          style={{ color: 'var(--color-dark-text-muted)' }}
        >
          Reference URL
        </label>
        <input
          id="puppet-url"
          type="url"
          value={url}
          onChange={(e) => handleUrlChange(e.target.value)}
          onBlur={handleBlur}
          className="w-full rounded-md border px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2"
          style={{
            backgroundColor: 'var(--color-dark-bg)',
            borderColor: 'var(--color-dark-border)',
            color: 'var(--color-dark-text)',
          }}
          placeholder="https://example.com"
        />
      </div>

      {/* Save indicator / button */}
      <div className="flex items-center justify-end gap-2">
        {hasChanges ? (
          <motion.button
            onClick={saveChanges}
            className="flex items-center gap-1 rounded-md px-3 py-1.5 text-xs font-medium"
            style={{
              backgroundColor: 'var(--color-body-head)',
              color: 'white',
            }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <Check className="h-3 w-3" />
            Save Changes
          </motion.button>
        ) : (
          <span
            className="flex items-center gap-1 text-xs"
            style={{ color: 'var(--color-dark-text-muted)' }}
          >
            <Check className="h-3 w-3" />
            Saved
          </span>
        )}
      </div>
    </div>
  );
}
